import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { API_BASE } from '../../../../../api';
import { useLogos } from '../../../../../context/LogoProvider';
import '../../../Styles/ColoresCatalog.css';

// Configuración de endpoint de subida (Ajustar si la ruta real es diferente)
const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
const UPLOAD_ENDPOINT = `${API_BASE}/upload/logo`;

export default function LogosModal({ open, mode, data, onClose, onSubmit, usedTypes = [] }) {
    const { getSystemConfig } = useLogos();
    const [tipos, setTipos] = useState([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Upload states
    const [uploadError, setUploadError] = useState('');
    const [previousImagePath, setPreviousImagePath] = useState(null);

    const [form, setForm] = useState({
        nombre: '',
        descripcion: '',
        idTipologo: '',
        imagenUrl: '',
        imagenUrlPreview: '',
        activo: true
    });

    // Fetch TipoLogos
    useEffect(() => {
        if (open) {
            const fetchTypes = async () => {
                setIsLoadingTypes(true);
                try {
                    const res = await axios.get(ENDPOINTS.tipoLogos);
                    const list = (res.data || []).map(t => ({
                        id: t.idTipoLogo || t.IdTipoLogo,
                        nombre: t.nombre || t.Nombre
                    }));
                    setTipos(list);
                } catch (e) {
                    console.error("Error loading TipoLogos:", e);
                } finally {
                    setIsLoadingTypes(false);
                }
            };
            fetchTypes();
        }
    }, [open]);

    // Init form
    useEffect(() => {
        if (open) {
            if (mode === 'edit' && data) {
                const initialForm = {
                    nombre: data.nombre || '',
                    descripcion: data.descripcion || '',
                    idTipologo: data.idTipologo || '',
                    imagenUrl: data.imagenUrl || '',
                    imagenUrlPreview: data.imagenUrl || '',
                    activo: data.activo ?? true
                };
                setForm(initialForm);
                if (initialForm.imagenUrl) {
                    setPreviousImagePath(initialForm.imagenUrl);
                }
            } else {
                setForm({
                    nombre: '',
                    descripcion: '',
                    idTipologo: '',
                    imagenUrl: '',
                    imagenUrlPreview: '',
                    activo: true
                });
                setPreviousImagePath(null);
            }
            setUploadError('');
        }
    }, [open, mode, data]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validaciones
        if (!file.type.startsWith('image/')) {
            setUploadError('Solo se permiten archivos de imagen.');
            return;
        }
        const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
        if (file.size > maxMB * 1024 * 1024) {
            setUploadError(`La imagen no debe superar los ${maxMB}MB.`);
            return;
        }

        setUploadError('');
        const blobUrl = URL.createObjectURL(file);

        // Preview inmediato
        setForm(prev => ({ ...prev, imagenUrlPreview: blobUrl }));
        setIsUploading(true);

        try {
            // Eliminar anterior si existe
            if (previousImagePath) {
                try {
                    // Intento de borrado
                    await axios.delete(UPLOAD_ENDPOINT, {
                        params: { filePath: previousImagePath },
                        withCredentials: true
                    });
                } catch (ignore) {
                    console.warn("Could not delete previous image:", ignore);
                }
            }

            const formData = new FormData();
            formData.append('file', file);

            // Subir
            const response = await axios.post(UPLOAD_ENDPOINT, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true
            });

            const filePath = response.data.filePath || response.data.FilePath;

            setPreviousImagePath(filePath);
            setForm(prev => ({
                ...prev,
                imagenUrl: filePath,
                imagenUrlPreview: blobUrl
            }));

        } catch (error) {
            console.error('Error uploading file:', error);
            setUploadError('Error al subir la imagen. Verifique su conexión.');
            setForm(prev => ({ ...prev, imagenUrlPreview: null }));
        } finally {
            setIsUploading(false);
        }
    };

    if (!open) return null;

    const isFormValid = form.nombre && form.idTipologo && form.imagenUrl && !isUploading;

    const labelStyle = {
        display: 'block',
        fontSize: '0.95rem',
        fontWeight: '800',
        color: '#0f172a',
        marginBottom: '0.5rem'
    };

    const Header = (
        <div
            className="roles-topbar"
            style={{
                marginBottom: '1.5rem',
                paddingRight: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flex: '0 0 auto',
            }}>
            <h3 className="roles-title" style={{ marginRight: 0 }}>
                {mode === 'new' ? 'Nuevo Logo' : 'Editar Logo'}
            </h3>
            <button className="roles-btn" onClick={onClose} aria-label="Cerrar">
                ✕
            </button>
        </div>
    );

    // Helper para URL de imagen
    const getImageUrl = () => {
        if (form.imagenUrlPreview && form.imagenUrlPreview.startsWith('blob:')) return form.imagenUrlPreview;

        if (form.imagenUrl) {
            return form.imagenUrl.startsWith('http')
                ? form.imagenUrl
                : `${SERVER_URL}${form.imagenUrl.startsWith('/') ? '' : '/'}${form.imagenUrl}`;
        }
        return null;
    };
    const currentImageUrl = getImageUrl();

    const Body = (
        <div
            style={{
                paddingLeft: '2rem',
                paddingRight: '2rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem',
                overflowY: 'auto',
                flex: '1 1 auto',
                minHeight: 0,
            }}>

            <div>
                <label style={labelStyle}>Nombre / Referencia:</label>
                <input
                    className="infoField"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Ej: Logo Header Dark"
                />
            </div>

            <div>
                <label style={labelStyle}>Descripción:</label>
                <input
                    className="infoField"
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Detalles sobre dónde se usa..."
                />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
                <div>
                    <label style={labelStyle}>Tipo de Logo:</label>
                    <select
                        className="infoField"
                        value={form.idTipologo}
                        onChange={(e) => handleChange('idTipologo', e.target.value)}
                        disabled={isLoadingTypes}
                    >
                        <option value="">-- Seleccionar Tipo --</option>
                        {tipos.map(t => {
                            const isUsed = usedTypes.includes(Number(t.id));
                            const isMyOriginalType = (mode === 'edit' && data && Number(data.idTipologo) === Number(t.id));

                            if (isUsed && !isMyOriginalType) return null;

                            return (
                                <option key={t.id} value={t.id}>
                                    {t.nombre}
                                </option>
                            );
                        })}
                    </select>
                </div>

                {/* File Upload UI */}
                <div>
                    <label style={labelStyle}>Imagen del Logo:</label>
                    <div
                        className={`file-uploader${form.imagenUrl ? ' file-uploader--filled' : ''}${uploadError ? ' file-uploader--error' : ''}`}
                    >
                        {currentImageUrl && (
                            <div className="file-uploader__preview">
                                <img src={currentImageUrl} alt="Preview" onError={(e) => { e.target.style.display = 'none'; }} />
                            </div>
                        )}

                        <label className="file-uploader__label">
                            <input type="file" accept="image/*" onChange={handleFileChange} onClick={(e) => { e.target.value = ''; }} style={{ display: 'none' }} />
                            <div className="file-uploader__icon">{form.imagenUrl ? '🔄' : '☁️'}</div>
                            <div className="file-uploader__text">{form.imagenUrl ? 'Cambiar imagen' : 'Clic para subir imagen'}</div>
                            <div className="file-uploader__hint">JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)</div>
                        </label>

                        {uploadError && (
                            <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>{uploadError}</div>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );

    const Footer = (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '.8rem',
                marginTop: 'auto',
                paddingTop: '2rem',
                paddingRight: '2rem',
                flex: '0 0 auto',
            }}
        >
            <button className="roles-btn" onClick={onClose}>Cancelar</button>
            <button
                className="btn-action"
                onClick={() => onSubmit(form, mode)}
                disabled={!isFormValid}
                style={{
                    opacity: !isFormValid ? 0.5 : 1,
                    cursor: !isFormValid ? 'not-allowed' : 'pointer',
                }}
            >
                {isUploading ? 'Subiendo...' : (mode === 'new' ? 'Guardar' : 'Actualizar')}
            </button>
        </div>
    );

    return (
        <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%', minHeight: '550px' }}>
            {Header}
            {Body}
            {Footer}
        </div>
    );
}
